import {TranslateService} from '@ngx-translate/core';
import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import * as XLSX from 'xlsx';

type PreviewCell = string | number | boolean | Date | null | undefined;

@Component({
    selector: 'dmp-excel-preview',
    templateUrl: './excel-preview.component.html',
    styleUrls: ['./excel-preview.component.scss'],
    standalone: false
})
export class ExcelPreviewComponent implements OnChanges {
    get locale(): string { return this.translate.getCurrentLang() === 'en' ? 'en-GB' : 'fr-FR'; }

    constructor(private readonly translate: TranslateService) {}

    @Input() fileBlob: Blob | null = null;
    @Input() fileName: string | null = null;
    @Input() maxRows = 120;
    @Input() maxColumns = 60;
    @Input() warningFileSizeMb = 8;
    @Input() hardLimitFileSizeMb = 25;

    loading = false;
    loadingMessage = this.translate.instant('invoicing.excel.loading');
    errorMessage: string | null = null;
    warningMessages: string[] = [];
    sheetOptions: Array<{ label: string; value: string }> = [];
    selectedSheetName: string | null = null;
    displayedRows: string[][] = [];
    totalRows = 0;
    totalColumns = 0;
    previewedRows = 0;
    previewedColumns = 0;
    fileSizeMb = 0;

    private workbook: XLSX.WorkBook | null = null;
    private fileWarningMessages: string[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.fileBlob) {
            void this.loadWorkbookFromBlob();
        }
    }

    onSheetSelection(sheetName?: string | null): void {
        this.selectedSheetName = sheetName || null;
        this.refreshPreview();
    }

    get displayedRowsCount(): number {
        return this.displayedRows.length;
    }

    get hasRowTruncation(): boolean {
        return this.totalRows > this.previewedRows;
    }

    get hasColumnTruncation(): boolean {
        return this.totalColumns > this.previewedColumns;
    }

    private async loadWorkbookFromBlob(): Promise<void> {
        this.resetState();
        if (!this.fileBlob) {
            return;
        }

        this.fileSizeMb = this.toMegabytes(this.fileBlob.size);
        const hardLimitBytes = this.hardLimitFileSizeMb * 1024 * 1024;
        if (this.hardLimitFileSizeMb > 0 && this.fileBlob.size > hardLimitBytes) {
            this.errorMessage = this.translate.instant('invoicing.excel.tooLarge', {size: this.fileSizeMb.toLocaleString(this.locale, {minimumFractionDigits: 1, maximumFractionDigits: 1}), limit: this.hardLimitFileSizeMb});
            return;
        }

        const warningLimitBytes = this.warningFileSizeMb * 1024 * 1024;
        if (this.warningFileSizeMb > 0 && this.fileBlob.size > warningLimitBytes) {
            this.fileWarningMessages.push(
                this.translate.instant('invoicing.excel.large', {size: this.fileSizeMb.toLocaleString(this.locale, {minimumFractionDigits: 1, maximumFractionDigits: 1})})
            );
        }
        this.warningMessages = [...this.fileWarningMessages];

        this.loading = true;
        this.loadingMessage = this.translate.instant('invoicing.excel.reading');
        try {
            await this.yieldToUi();
            const buffer = await this.fileBlob.arrayBuffer();
            this.loadingMessage = this.translate.instant('invoicing.excel.analysing');
            await this.yieldToUi();
            this.workbook = XLSX.read(buffer, {type: 'array', cellDates: true, dense: true});
            const names = this.workbook.SheetNames || [];
            this.sheetOptions = names.map((name) => ({label: name, value: name}));
            this.selectedSheetName = names.length > 0 ? names[0] : null;
            this.loadingMessage = this.translate.instant('invoicing.excel.preparing');
            await this.yieldToUi();
            this.refreshPreview();
        } catch (_error) {
            this.errorMessage = this.translate.instant('invoicing.excel.readFailed');
        } finally {
            this.loading = false;
            this.loadingMessage = this.translate.instant('invoicing.excel.loading');
        }
    }

    private refreshPreview(): void {
        this.errorMessage = null;
        this.displayedRows = [];
        this.totalRows = 0;
        this.totalColumns = 0;
        this.previewedRows = 0;
        this.previewedColumns = 0;
        this.warningMessages = [...this.fileWarningMessages];

        if (!this.workbook || !this.selectedSheetName) {
            return;
        }

        const worksheet = this.workbook.Sheets[this.selectedSheetName];
        if (!worksheet) {
            this.errorMessage = this.translate.instant('invoicing.excel.sheetMissing');
            return;
        }

        const ref = worksheet['!ref'];
        if (!ref) {
            return;
        }

        const decoded = XLSX.utils.decode_range(ref);
        const startRow = decoded.s.r;
        const startColumn = decoded.s.c;
        const endRow = decoded.e.r;
        const endColumn = decoded.e.c;
        this.totalRows = endRow - startRow + 1;
        this.totalColumns = endColumn - startColumn + 1;

        const previewEndRow = Math.min(endRow, startRow + Math.max(this.maxRows, 1) - 1);
        const previewEndColumn = Math.min(endColumn, startColumn + Math.max(this.maxColumns, 1) - 1);
        const previewRange = {
            s: {r: startRow, c: startColumn},
            e: {r: previewEndRow, c: previewEndColumn}
        };

        const rows = XLSX.utils.sheet_to_json<PreviewCell[]>(worksheet, {
            header: 1,
            raw: false,
            defval: '',
            range: previewRange
        });
        const previewWidth = previewEndColumn >= startColumn ? (previewEndColumn - startColumn + 1) : 0;
        const normalizedRows = rows.map((row) => this.normalizeRow(row, previewWidth));

        this.previewedRows = normalizedRows.length;
        this.previewedColumns = previewWidth;
        this.displayedRows = normalizedRows;

        if (this.hasRowTruncation) {
            this.warningMessages.push(
                this.translate.instant('invoicing.excel.rowsLimited', {shown: this.previewedRows, total: this.totalRows})
            );
        }
        if (this.hasColumnTruncation) {
            this.warningMessages.push(
                this.translate.instant('invoicing.excel.columnsLimited', {shown: this.previewedColumns, total: this.totalColumns})
            );
        }
    }

    private normalizeRow(row: PreviewCell[] = [], width: number): string[] {
        const normalized: string[] = new Array(width).fill('');
        for (let i = 0; i < width; i++) {
            normalized[i] = this.formatCellValue(row[i]);
        }
        return normalized;
    }

    private formatCellValue(value: PreviewCell): string {
        if (value === null || value === undefined) {
            return '';
        }
        if (value instanceof Date) {
            return value.toLocaleString(this.locale);
        }
        return String(value);
    }

    private resetState(): void {
        this.loading = false;
        this.errorMessage = null;
        this.sheetOptions = [];
        this.selectedSheetName = null;
        this.displayedRows = [];
        this.totalRows = 0;
        this.totalColumns = 0;
        this.previewedRows = 0;
        this.previewedColumns = 0;
        this.fileSizeMb = 0;
        this.warningMessages = [];
        this.fileWarningMessages = [];
        this.workbook = null;
    }

    private toMegabytes(bytes: number): number {
        return bytes / (1024 * 1024);
    }

    private async yieldToUi(): Promise<void> {
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }
}
